import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSearch, FaTimes } from "react-icons/fa";
import { ConversationDto, MessageSearchResultDto } from "../../models/MessageModel";
import { deleteConversation, deleteConversations } from "../../services/messageService";
import { stripMarkup, buildSnippet } from "../../utils/markupUtils";
import { parseServerDate } from "../../utils/dateUtils";

export interface ConversationSearchState {
  input: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  onClear: () => void;
  isActive: boolean;
  results: MessageSearchResultDto[];
  loading: boolean;
  error: string | null;
  totalItems: number;
  hasMore: boolean;
  onLoadMore: () => void;
  onSelectResult: (result: MessageSearchResultDto) => void;
}

interface ConversationListProps {
  conversations: ConversationDto[];
  selectedId: number | null;
  loading: boolean;
  hasMore: boolean;
  onSelect: (conversationId: number) => void;
  onLoadMore: () => void;
  onDelete: (conversationId: number) => void;
  onBulkDelete: (ids: number[]) => void;
  // Search state is lifted to MessagesPage so it can coordinate with the right panel
  search: ConversationSearchState;
}

interface DeleteConversationModalProps {
  username: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Custom confirmation modal for deleting a single conversation.
 * Replaces the native browser confirm() dialog.
 */
const DeleteConversationModal: React.FC<DeleteConversationModalProps> = ({
  username,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  return (
    <div className="msg-confirm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="msg-confirm-modal">
        <h4 className="msg-confirm-title">
          {t("messages_delete_conversation_title")}
        </h4>
        <p className="msg-confirm-question">
          <strong>{username}</strong>{" "}
          {t("messages_delete_conversation_confirm")}
        </p>
        <p className="msg-confirm-note">
          ℹ️ {t("messages_delete_conversation_note")}
        </p>
        <div className="msg-confirm-actions">
          <button className="msg-confirm-cancel" onClick={onCancel}>
            {t("admin_cancel")}
          </button>
          <button className="msg-confirm-delete" onClick={onConfirm}>
            {t("admin_action_delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

interface BulkDeleteModalProps {
  count: number;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation modal for bulk-deleting multiple conversations.
 */
const BulkDeleteModal: React.FC<BulkDeleteModalProps> = ({
  count,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  return (
    <div className="msg-confirm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="msg-confirm-modal">
        <h4 className="msg-confirm-title">
          {t("messages_bulk_delete_title")}
        </h4>
        <p className="msg-confirm-question">
          {t("messages_bulk_delete_confirm", { count })}
        </p>
        <p className="msg-confirm-note">
          ℹ️ {t("messages_bulk_delete_note")}
        </p>
        <div className="msg-confirm-actions">
          <button className="msg-confirm-cancel" onClick={onCancel}>
            {t("admin_cancel")}
          </button>
          <button className="msg-confirm-delete" onClick={onConfirm}>
            {t("admin_action_delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Minimal date formatter for search result rows.
 * Mirrors the conversation-item formatTime logic but lives here so the
 * helper is available in both normal-mode and search-mode rendering.
 */
function formatSearchResultTime(t: (key: string) => string, dateStr: string): string {
  const date = parseServerDate(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else if (diffDays === 1) {
    return t("messages_yesterday");
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: "short" });
  } else {
    return date.toLocaleDateString([], { day: "numeric", month: "short" });
  }
}

/**
 * Scrollable list of conversations with single and bulk delete support,
 * plus an inbox-wide search bar at the top.
 *
 * Single delete  : ⋮ button per item → confirmation modal.
 * Bulk delete    : "Seç" toggle (only shown when >1 conversations) →
 *                  two-row select bar appears → select items →
 *                  "Seçilenleri Sil" button.
 *
 * Search         : the input at the top queries ALL messages in the user's
 *                  inbox. While search results are displayed they replace
 *                  the conversation list. Clicking a result opens the
 *                  underlying conversation in the right panel.
 *
 * Select bar layout (two rows so it fits the narrow 320px panel):
 *   Row 1 — [☑] Tümünü Seç                  [İptal]
 *   Row 2 — X konuşma seçildi    [Seçilenleri Sil]   (only when count > 0)
 */
export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedId,
  loading,
  hasMore,
  onSelect,
  onLoadMore,
  onDelete,
  onBulkDelete,
  search,
}) => {
  const { t } = useTranslation();

  const [pendingDelete, setPendingDelete] = useState<{
    id: number;
    username: string;
  } | null>(null);

  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Multi-select is only available when there are at least 2 conversations.
  // With a single conversation the ⋮ button is sufficient for deletion.
  const canUseSelectMode = conversations.length > 1;

  const handleDeleteConfirm = async () => {
    if (!pendingDelete) return;
    try {
      await deleteConversation(pendingDelete.id);
      onDelete(pendingDelete.id);
    } catch (error) {
      console.error("Failed to delete conversation:", error);
    } finally {
      setPendingDelete(null);
    }
  };

  const enterSelectMode = () => {
    setIsSelectMode(true);
    setSelectedIds(new Set());
  };

  const exitSelectMode = () => {
    setIsSelectMode(false);
    setSelectedIds(new Set());
  };

  const toggleSelectItem = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const isAllSelected =
    conversations.length > 0 && selectedIds.size === conversations.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(conversations.map((c) => c.id)));
    }
  };

  const handleBulkDeleteConfirm = async () => {
    const ids = Array.from(selectedIds);
    try {
      await deleteConversations(ids);
      onBulkDelete(ids);
      exitSelectMode();
    } catch (error) {
      console.error("Failed to bulk delete conversations:", error);
    } finally {
      setShowBulkDeleteModal(false);
    }
  };

  const formatTime = (dateStr: string | null): string => {
    if (!dateStr) return "";
    const date = parseServerDate(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } else if (diffDays === 1) {
      return t("messages_yesterday");
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    } else {
      return date.toLocaleDateString([], { day: "numeric", month: "short" });
    }
  };

  // ─── Search input handlers ────────────────────────────────────────────────

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      search.onSubmit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      search.onClear();
    }
  };

  // Render the search bar above every other list content so it's always accessible
  const searchBar = (
    <div className="conversation-search-bar">
      <div className="conversation-search-input-row">
        <FaSearch className="conversation-search-icon" size={12} />
        <input
          type="text"
          className="conversation-search-input"
          value={search.input}
          onChange={(e) => search.onInputChange(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          placeholder={t("messages_search_inbox_placeholder")}
        />
        {(search.input.length > 0 || search.isActive) && (
          <button
            type="button"
            className="conversation-search-clear-btn"
            onClick={search.onClear}
            title={t("messages_search_clear")}
            aria-label={t("messages_search_clear")}
          >
            <FaTimes size={10} />
          </button>
        )}
        <button
          type="button"
          className="conversation-search-go-btn"
          onClick={search.onSubmit}
          disabled={search.loading || search.input.trim().length < 2}
        >
          {search.loading ? "..." : t("messages_search_go")}
        </button>
      </div>
      {search.error && (
        <div className="conversation-search-info conversation-search-info--error">
          {search.error}
        </div>
      )}
      {!search.error && search.isActive && (
        <div className="conversation-search-info">
          {search.totalItems > 0
            ? t("messages_search_results_count", { count: search.totalItems })
            : t("messages_search_no_results")}
        </div>
      )}
    </div>
  );

  // ─── Empty / loading states (only apply when NOT in search mode) ─────────

  if (!search.isActive && loading && conversations.length === 0) {
    return (
      <>
        {searchBar}
        <div className="conversation-list-empty">{t("loading")}</div>
      </>
    );
  }

  if (!search.isActive && conversations.length === 0) {
    return (
      <>
        {searchBar}
        <div className="conversation-list-empty">
          {t("messages_no_conversations")}
        </div>
      </>
    );
  }

  // ─── Search-mode rendering ───────────────────────────────────────────────

  if (search.isActive) {
    return (
      <>
        {searchBar}

        <div className="conversation-list">
          {search.results.map((r) => {
            const initial = r.otherUsername.charAt(0).toUpperCase();
            const snippet = buildSnippet(r.content);

            return (
              <div
                key={r.messageId}
                className="conversation-item conversation-search-result-item"
                onClick={() => search.onSelectResult(r)}
              >
                <div className="conversation-avatar">
                  {r.otherUserAvatarUrl ? (
                    <img src={r.otherUserAvatarUrl} alt={r.otherUsername} />
                  ) : (
                    initial
                  )}
                </div>

                <div className="conversation-info">
                  <div className="conversation-top-row">
                    <span className="conversation-username">
                      {r.otherUsername}
                    </span>
                    <span className="conversation-time">
                      {formatSearchResultTime(t, r.createdAt)}
                    </span>
                  </div>
                  <div className="conversation-bottom-row">
                    <span className="conversation-preview">{snippet}</span>
                  </div>
                </div>
              </div>
            );
          })}

          {search.hasMore && (
            <div className="conversation-load-more">
              <button onClick={search.onLoadMore} disabled={search.loading}>
                {search.loading ? t("loading") : t("load_more")}
              </button>
            </div>
          )}
        </div>
      </>
    );
  }

  // ─── Normal-mode rendering ───────────────────────────────────────────────

  return (
    <>
      {searchBar}

      {/*
       * Select mode toolbar — only rendered when there are ≥2 conversations.
       * Two-row layout so it fits cleanly in the narrow (320px) left panel:
       *   Row 1: [☑] Tümünü Seç  ·············  İptal
       *   Row 2: X konuşma seçildi  ·  [Seçilenleri Sil]   ← only when count > 0
       */}
      {canUseSelectMode && (
        <div className="conversation-select-bar">
          {isSelectMode ? (
            <>
              {/* Row 1 — select-all + cancel */}
              <div className="conversation-select-row">
                <label className="conversation-select-all-label">
                  <input
                    type="checkbox"
                    className="conversation-checkbox"
                    checked={isAllSelected}
                    onChange={toggleSelectAll}
                  />
                  <span className="conversation-select-all-text">
                    {t("messages_select_all")}
                  </span>
                </label>
                <button
                  className="conversation-select-cancel-btn"
                  onClick={exitSelectMode}
                >
                  {t("messages_select_cancel")}
                </button>
              </div>

              {/* Row 2 — count + delete button, only when something is selected */}
              {selectedIds.size > 0 && (
                <div className="conversation-select-row conversation-select-action-row">
                  <span className="conversation-selected-count">
                    {t("messages_selected_count", { count: selectedIds.size })}
                  </span>
                  <button
                    className="conversation-bulk-delete-btn"
                    onClick={() => setShowBulkDeleteModal(true)}
                  >
                    {t("messages_delete_selected")}
                  </button>
                </div>
              )}
            </>
          ) : (
            /* Normal mode — show "Seç" toggle aligned to the right */
            <div className="conversation-select-row conversation-select-row--end">
              <button
                className="conversation-select-toggle-btn"
                onClick={enterSelectMode}
              >
                {t("messages_select_mode")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Conversation items */}
      <div className="conversation-list">
        {conversations.map((conv) => {
          const isSelected = conv.id === selectedId;
          const isChecked = selectedIds.has(conv.id);
          const hasUnread = conv.unreadCount > 0;
          const initial = conv.otherUsername.charAt(0).toUpperCase();
          const previewText = conv.lastMessagePreview
            ? stripMarkup(conv.lastMessagePreview)
            : t("messages_no_messages_yet");

          return (
            <div
              key={conv.id}
              className={[
                "conversation-item",
                isSelected && !isSelectMode ? "selected" : "",
                hasUnread ? "unread" : "",
                isSelectMode && isChecked ? "select-mode-checked" : "",
                isSelectMode ? "select-mode" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => {
                if (isSelectMode) {
                  toggleSelectItem(conv.id);
                } else {
                  onSelect(conv.id);
                }
              }}
            >
              {isSelectMode && (
                <input
                  type="checkbox"
                  className="conversation-checkbox"
                  checked={isChecked}
                  onChange={() => toggleSelectItem(conv.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}

              <div className="conversation-avatar">
                {conv.otherUserAvatarUrl ? (
                  <img src={conv.otherUserAvatarUrl} alt={conv.otherUsername} />
                ) : (
                  initial
                )}
              </div>

              <div className="conversation-info">
                {/* Top row: username + timestamp */}
                <div className="conversation-top-row">
                  <span className="conversation-username">
                    {conv.otherUsername}
                  </span>
                  <span className="conversation-time">
                    {formatTime(conv.lastMessageAt)}
                  </span>
                </div>

                {/* Bottom row: preview + unread badge */}
                <div className="conversation-bottom-row">
                  <span
                    className={`conversation-preview ${
                      hasUnread ? "unread-text" : ""
                    }`}
                  >
                    {previewText}
                  </span>
                  {hasUnread && (
                    <span className="conversation-unread-badge">
                      {conv.unreadCount > 99 ? "99+" : conv.unreadCount}
                    </span>
                  )}
                </div>
              </div>

              {/*
               * Vertical three-dot menu button (⋮).
               * Hidden in select mode — clicking items toggles selection instead.
               * No title prop → no native browser tooltip.
               */}
              {!isSelectMode && (
                <button
                  className="conversation-options-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingDelete({ id: conv.id, username: conv.otherUsername });
                  }}
                >
                  ⋮
                </button>
              )}
            </div>
          );
        })}

        {hasMore && (
          <div className="conversation-load-more">
            <button onClick={onLoadMore}>{t("load_more")}</button>
          </div>
        )}
      </div>

      {pendingDelete && (
        <DeleteConversationModal
          username={pendingDelete.username}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      {showBulkDeleteModal && (
        <BulkDeleteModal
          count={selectedIds.size}
          onConfirm={handleBulkDeleteConfirm}
          onCancel={() => setShowBulkDeleteModal(false)}
        />
      )}
    </>
  );
};